import { useEffect, useMemo, useRef, useState } from 'react';
import { Job } from '../types';
import { useJobs } from './useJobs';

interface UseOptimisticJobsParams {
  accountId?: string;
  filter: string;
  search?: string;
  identityFilter?: string;
  sort: {
    column: string | null;
    direction: 'asc' | 'desc' | null;
  };
}

interface UseOptimisticJobsOptions {
  enableRefresh?: boolean;
}

export function useOptimisticJobs(
  params: UseOptimisticJobsParams,
  options: UseOptimisticJobsOptions = {}
) {
  // Map table params to useJobs params
  const jobsParams = useMemo(() => {
    return {
      ...(params.filter !== 'all' && { accountId: params.accountId }),
      perPage: 25,
      onlyCurrentAccount: params.filter === 'current',
      displayAll: params.filter === 'all',
      query: params.search || undefined,
    };
  }, [params.accountId, params.filter, params.search]);

  const {
    data: serverData,
    isLoading: serverLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useJobs(jobsParams, options);

  // Track previous successful data
  const previousDataRef = useRef<Job[]>([]);
  const previousParamsRef = useRef<UseOptimisticJobsParams | null>(null);
  const [isOptimisticFiltering, setIsOptimisticFiltering] = useState(false);

  // Process infinite query data into flat array
  const processedServerData = useMemo(() => {
    if (!serverData?.pages) return [];
    return serverData.pages.flatMap((page) => page.items);
  }, [serverData]);

  // Get pagination data from the first page (all pages should have same total count)
  const paginationData = useMemo(() => {
    return serverData?.pages?.[0]?.pagination || null;
  }, [serverData]);

  // Update previous data when we get new server data
  useEffect(() => {
    if (!serverLoading && processedServerData.length > 0) {
      previousDataRef.current = processedServerData;
      setIsOptimisticFiltering(false);
    }
  }, [processedServerData, serverLoading]);

  // Check if params changed (indicating a new filter/search)
  const paramsChanged = useMemo(() => {
    if (!previousParamsRef.current) return false;

    const prev = previousParamsRef.current;
    return (
      prev.filter !== params.filter ||
      prev.search !== params.search ||
      prev.identityFilter !== params.identityFilter ||
      prev.accountId !== params.accountId
    );
  }, [params.filter, params.search, params.identityFilter, params.accountId]);

  // Update params ref
  useEffect(() => {
    previousParamsRef.current = params;
  }, [params]);

  // Client-side filtering logic
  const applyClientSideFilters = useMemo(() => {
    return (data: Job[]) => {
      let filtered = [...data];

      // Apply identity filtering if identity filter is provided
      if (params.identityFilter) {
        filtered = filtered.filter((job) => job.identity_id === params.identityFilter);
      }

      // Apply sorting
      if (params.sort.column && params.sort.direction) {
        const { column, direction } = params.sort;

        // Map column names to their corresponding Job property names
        const columnToPropertyMap: Record<string, keyof Job> = {
          name: 'p2p_name',
          status: 'p2p_status',
        };

        filtered = [...filtered].sort((a, b) => {
          // Get the property name from the map or use the column name directly
          const propertyName = columnToPropertyMap[column] || column;
          const valueA = a[propertyName as keyof Job];
          const valueB = b[propertyName as keyof Job];

          // Compare values based on direction
          if (typeof valueA === 'number' && typeof valueB === 'number') {
            return direction === 'asc' ? valueA - valueB : valueB - valueA;
          }

          // String comparison
          const compareResult = String(valueA).localeCompare(String(valueB));
          return direction === 'asc' ? compareResult : -compareResult;
        });
      }

      return filtered;
    };
  }, [params.identityFilter, params.sort]);

  // Determine if we should show optimistic data
  const shouldShowOptimistic = useMemo(() => {
    return (
      paramsChanged &&
      serverLoading &&
      previousDataRef.current.length > 0 &&
      !params.identityFilter // Don't show optimistic for identity filters since we can't filter client-side
    );
  }, [paramsChanged, serverLoading, params.identityFilter]);

  // Set optimistic filtering state
  useEffect(() => {
    if (shouldShowOptimistic) {
      setIsOptimisticFiltering(true);
    }
  }, [shouldShowOptimistic]);

  // Calculate final data to show
  const displayData = useMemo(() => {
    if (shouldShowOptimistic) {
      // Show optimistic filtered data from previous results
      return applyClientSideFilters(previousDataRef.current);
    }

    // Show server data (could be empty during initial load)
    return applyClientSideFilters(processedServerData);
  }, [shouldShowOptimistic, applyClientSideFilters, processedServerData]);

  // Calculate loading states
  const isInitialLoading = serverLoading && previousDataRef.current.length === 0;

  return {
    data: displayData,
    pagination: paginationData,
    isLoading: isInitialLoading, // Only show skeleton on initial load
    isOptimisticFiltering, // Background loading indicator
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
}
